import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../AdminDashboard.css';

function generateRandomPassword() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  const bytes = new Uint32Array(12);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => chars[b % chars.length]).join('');
}

function UserManagementPanel({ user, apiUrl }) {
  const [users, setUsers] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'student',
    access_until: '',
    access_tier: 'platform_only',
  });
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [loginEvents, setLoginEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${apiUrl}/admin/users`);
      setUsers(response.data);
    } catch (err) {
      setMessage('Failed to load users');
      setMessageType('error');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setMessage('');
    setCreatedCredentials(null);

    if (!formData.email || !formData.password || !formData.full_name) {
      setMessage('All fields are required');
      setMessageType('error');
      return;
    }

    try {
      await axios.post(`${apiUrl}/admin/users`, {
        ...formData,
        access_until: formData.access_until || null,
      });
      const roleLabel = formData.role === 'tutor' ? 'Tutor' : formData.role === 'admin' ? 'Admin' : 'Student';
      setMessage(`${roleLabel} created successfully!`);
      setMessageType('success');
      setCreatedCredentials({ email: formData.email, password: formData.password, full_name: formData.full_name });
      setFormData({ email: '', password: '', full_name: '', role: 'student', access_until: '', access_tier: 'platform_only' });
      setShowCreateForm(false);
      setShowPassword(false);
      fetchUsers();
    } catch (err) {
      setMessage(err.response?.data?.error || 'Failed to create user');
      setMessageType('error');
    }
  };

  const handleToggleStatus = async (u) => {
    try {
      await axios.patch(`${apiUrl}/admin/users/${u.id}/status`, { is_active: !u.is_active });
      setMessage(u.is_active ? `${u.full_name} has been unenrolled — they can no longer sign in.` : `${u.full_name} has been re-enrolled.`);
      setMessageType('success');
      fetchUsers();
    } catch (err) {
      setMessage(err.response?.data?.error || 'Failed to update access status');
      setMessageType('error');
    }
  };

  const handleAccessDateChange = async (u, dateStr) => {
    try {
      await axios.patch(`${apiUrl}/admin/users/${u.id}/access`, { access_until: dateStr || null });
      setMessage(dateStr ? `Access for ${u.full_name} now ends ${dateStr}.` : `Access cutoff cleared for ${u.full_name}.`);
      setMessageType('success');
      fetchUsers();
    } catch (err) {
      setMessage(err.response?.data?.error || 'Failed to update access date');
      setMessageType('error');
    }
  };

  const handleAccessTierChange = async (u, tier) => {
    try {
      await axios.patch(`${apiUrl}/admin/users/${u.id}/access-tier`, { access_tier: tier });
      setMessage(`${u.full_name}'s access tier set to ${tier === 'ucat_excellence' ? 'UCAT Excellence' : 'Platform Only'}.`);
      setMessageType('success');
      fetchUsers();
    } catch (err) {
      setMessage(err.response?.data?.error || 'Failed to update access tier');
      setMessageType('error');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const generatePassword = () => {
    setFormData((prev) => ({ ...prev, password: generateRandomPassword() }));
    setShowPassword(true);
  };

  const copyCredentials = () => {
    if (!createdCredentials) return;
    const text = `Username: ${createdCredentials.email}\nPassword: ${createdCredentials.password}`;
    navigator.clipboard?.writeText(text).catch(() => {});
  };

  const openResetPassword = (u) => {
    setResetTarget(u);
    setResetPassword(generateRandomPassword());
    setMessage('');
  };

  const toggleLoginActivity = async (u) => {
    if (expandedUserId === u.id) {
      setExpandedUserId(null);
      return;
    }
    setExpandedUserId(u.id);
    setLoadingEvents(true);
    try {
      const response = await axios.get(`${apiUrl}/admin/users/${u.id}/login-events`);
      setLoginEvents(response.data);
    } catch (err) {
      setLoginEvents([]);
    } finally {
      setLoadingEvents(false);
    }
  };

  const confirmResetPassword = async () => {
    try {
      await axios.patch(`${apiUrl}/admin/users/${resetTarget.id}/reset-password`, { password: resetPassword });
      setMessage(`Password reset for ${resetTarget.full_name}. Give them the new password below — copy it now.`);
      setMessageType('success');
      setCreatedCredentials({ email: resetTarget.email, password: resetPassword, full_name: resetTarget.full_name });
      setResetTarget(null);
    } catch (err) {
      setMessage(err.response?.data?.error || 'Failed to reset password');
      setMessageType('error');
    }
  };

  return (
    <div className="panel admin-dashboard">
      <h2>User Management</h2>

      {message && (
        <div className={messageType === 'error' ? 'error' : 'success'}>
          {message}
        </div>
      )}

      <div className="create-student-section">
        <div className="section-header">
          <h2>Registered Users ({users.length})</h2>
          <button className="btn btn-primary" onClick={() => setShowCreateForm(!showCreateForm)}>
            {showCreateForm ? '× Close' : '+ New User'}
          </button>
        </div>

        {showCreateForm && (
          <form className="create-form" onSubmit={handleCreateUser}>
            <div className="form-row">
              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleInputChange}
                  placeholder="Full name"
                  required
                />
              </div>
              <div className="form-group">
                <label>Username</label>
                <input
                  type="text"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Username"
                  required
                />
              </div>
              <div className="form-group">
                <label>Role</label>
                <select name="role" value={formData.role} onChange={handleInputChange}>
                  <option value="student">Student</option>
                  <option value="tutor">Tutor (can see all answers)</option>
                  <option value="admin">Admin (full access)</option>
                </select>
              </div>
              <div className="form-group">
                <label>Password</label>
                <div className="password-field">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Create password"
                    required
                  />
                  <button type="button" className="btn btn-secondary" onClick={generatePassword}>
                    Generate
                  </button>
                </div>
              </div>
              {formData.role === 'student' && (
                <div className="form-group">
                  <label>Access Tier</label>
                  <select name="access_tier" value={formData.access_tier} onChange={handleInputChange}>
                    <option value="platform_only">Platform Only</option>
                    <option value="ucat_excellence">UCAT Excellence Program</option>
                  </select>
                </div>
              )}
              <div className="form-group">
                <label>Access Until (optional)</label>
                <input
                  type="date"
                  name="access_until"
                  value={formData.access_until}
                  onChange={handleInputChange}
                />
                <span className="field-hint">e.g. their UCAT exam date — access ends automatically after this date</span>
              </div>
            </div>
            <button type="submit" className="btn btn-primary">
              Create Account
            </button>
          </form>
        )}

        {resetTarget && (
          <div className="credentials-panel">
            <h3>Reset password for {resetTarget.full_name}</h3>
            <p>A new password has been generated. Confirm to apply it — the old password stops working immediately.</p>
            <div className="password-field">
              <input type="text" value={resetPassword} onChange={(e) => setResetPassword(e.target.value)} />
              <button type="button" className="btn btn-secondary" onClick={() => setResetPassword(generateRandomPassword())}>
                Regenerate
              </button>
            </div>
            <div className="credentials-actions">
              <button className="btn btn-secondary" onClick={() => setResetTarget(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={confirmResetPassword}>Confirm Reset</button>
            </div>
          </div>
        )}

        {createdCredentials && (
          <div className="credentials-panel">
            <h3>Give these credentials to {createdCredentials.full_name}</h3>
            <p>They won't be shown again — copy them now.</p>
            <div className="credentials-row"><strong>Username:</strong> {createdCredentials.email}</div>
            <div className="credentials-row"><strong>Password:</strong> {createdCredentials.password}</div>
            <div className="credentials-actions">
              <button className="btn btn-secondary" onClick={copyCredentials}>Copy</button>
              <button className="btn btn-secondary" onClick={() => setCreatedCredentials(null)}>Dismiss</button>
            </div>
          </div>
        )}
      </div>

      <div className="students-section">
        {users.length === 0 ? (
          <div className="empty-state">
            <p>No users yet. Create a student or tutor account above.</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th>Full Name</th>
                  <th>Username</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Access Tier</th>
                  <th>Access Until</th>
                  <th>Registration Date</th>
                  <th>Devices (30d)</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const isSelf = u.id === user.id;
                  const deviceCount = Number(u.recent_devices) || 0;
                  return (
                    <React.Fragment key={u.id}>
                    <tr>
                      <td>{u.full_name}{isSelf && ' (you)'}</td>
                      <td>{u.email}</td>
                      <td className="role-cell">{u.role}</td>
                      <td>
                        <span className={u.is_active ? 'status-badge status-active' : 'status-badge status-inactive'}>
                          {u.is_active ? 'Active' : 'Unenrolled'}
                        </span>
                      </td>
                      <td>
                        {u.role === 'student' ? (
                          <select
                            className="access-date-input"
                            value={u.access_tier}
                            disabled={isSelf}
                            onChange={(e) => handleAccessTierChange(u, e.target.value)}
                          >
                            <option value="platform_only">Platform Only</option>
                            <option value="ucat_excellence">UCAT Excellence</option>
                          </select>
                        ) : (
                          <span className="field-hint">n/a</span>
                        )}
                      </td>
                      <td>
                        <input
                          type="date"
                          className="access-date-input"
                          defaultValue={u.access_until ? u.access_until.slice(0, 10) : ''}
                          disabled={isSelf}
                          onBlur={(e) => {
                            const val = e.target.value;
                            const original = u.access_until ? u.access_until.slice(0, 10) : '';
                            if (val !== original) handleAccessDateChange(u, val);
                          }}
                        />
                      </td>
                      <td>{new Date(u.created_at).toLocaleDateString()}</td>
                      <td>
                        {u.last_login ? (
                          <button
                            type="button"
                            className={`device-count-badge ${deviceCount > 1 ? 'device-count-warning' : ''}`}
                            onClick={() => toggleLoginActivity(u)}
                            title="View recent login activity"
                          >
                            {deviceCount} {deviceCount === 1 ? 'device' : 'devices'}
                          </button>
                        ) : (
                          <span className="field-hint">Never signed in</span>
                        )}
                      </td>
                      <td className="table-actions">
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => openResetPassword(u)}
                          disabled={isSelf}
                        >
                          Reset Password
                        </button>
                        <button
                          className={`btn btn-sm ${u.is_active ? 'btn-danger' : 'btn-secondary'}`}
                          onClick={() => handleToggleStatus(u)}
                          disabled={isSelf}
                        >
                          {u.is_active ? 'Unenroll' : 'Re-enroll'}
                        </button>
                      </td>
                    </tr>
                    {expandedUserId === u.id && (
                      <tr className="login-activity-row">
                        <td colSpan={9}>
                          <div className="login-activity-panel">
                            <strong>Recent sign-ins for {u.full_name}</strong>
                            {deviceCount > 1 && (
                              <p className="login-activity-flag">
                                ⚠ {deviceCount} different IP addresses in the last 30 days — worth checking this account isn't being shared.
                              </p>
                            )}
                            {loadingEvents ? (
                              <p className="field-hint">Loading...</p>
                            ) : loginEvents.length === 0 ? (
                              <p className="field-hint">No login history recorded yet.</p>
                            ) : (
                              <table className="table login-activity-table">
                                <thead>
                                  <tr>
                                    <th>When</th>
                                    <th>IP address</th>
                                    <th>Device / browser</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {loginEvents.map((ev, idx) => (
                                    <tr key={idx}>
                                      <td>{new Date(ev.created_at).toLocaleString()}</td>
                                      <td>{ev.ip_address || 'unknown'}</td>
                                      <td className="login-activity-ua">{ev.user_agent || 'unknown'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default UserManagementPanel;
